import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';

import Text from './Text';

type Props = {
  children: React.ReactNode;
  label?: string;
  tooltip?: string;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  'aria-label'?: string;
};

export default function IconButton({
  children,
  label,
  tooltip,
  selected,
  disabled,
  onClick,
  ...rest
}: Props) {
  const [hovered, setHovered] = React.useState(false);

  return (
    <html.div style={Style.positionRelative}>
      <html.button
        style={[
          Style.row,
          Style.alignCenter,
          styles.base,
          !label && Style.centerBoth,
          selected && styles.selected,
          disabled && styles.disabled,
        ]}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        aria-label={rest['aria-label'] ?? tooltip}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {children}
        {label && (
          <Text typeface="body.regular" color="neutral80">
            {label}
          </Text>
        )}
      </html.button>
      {tooltip && hovered && (
        <html.div style={[Style.row, Style.centerBoth, styles.tooltip]}>
          <Text typeface="body.small.medium" color="neutral0">
            {tooltip}
          </Text>
        </html.div>
      )}
    </html.div>
  );
}

const styles = css.create({
  base: {
    gap: 4,
    minWidth: 40,
    minHeight: 40,
    paddingInline: 10,
    paddingBlock: 10,
    borderWidth: 0,
    borderRadius: 8,
    backgroundColor: 'transparent',
    color: color.neutral80,
    fontSize: 16,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    ':hover': {
      backgroundColor: color.neutral10,
    },
  },
  selected: {
    backgroundColor: color.neutral20,
    ':hover': {
      backgroundColor: color.neutral20,
    },
  },
  disabled: {
    color: color.neutral40,
    cursor: 'default',
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  tooltip: {
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginBottom: 4,
    paddingInline: 6,
    paddingBlock: 2,
    backgroundColor: color.neutral80,
    borderRadius: 8,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  },
});
